const int16ToFloat32 = (bytes: Uint8Array): Float32Array => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const samples = new Float32Array(Math.floor(bytes.byteLength / 2));
  for (let index = 0; index < samples.length; index += 1) {
    samples[index] = view.getInt16(index * 2, true) / 32768;
  }
  return samples;
};

const resampleLinear = (input: Float32Array, sourceRate: number, targetRate: number): Float32Array => {
  if (input.length === 0 || sourceRate <= 0 || targetRate <= 0 || sourceRate === targetRate) {
    return input;
  }

  const ratio = sourceRate / targetRate;
  const outputLength = Math.max(1, Math.round(input.length / ratio));
  const output = new Float32Array(outputLength);
  for (let index = 0; index < outputLength; index += 1) {
    const position = index * ratio;
    const left = Math.floor(position);
    const right = Math.min(left + 1, input.length - 1);
    const mix = position - left;
    output[index] = (input[left] * (1 - mix)) + (input[right] * mix);
  }
  return output;
};

export class PcmStreamPlayer {
  private context: AudioContext | null = null;
  private gain: GainNode | null = null;
  private nextTime = 0;
  private leftover = new Uint8Array(0);
  private pending = 0;
  private sampleRate = 24000;
  private endedResolvers: Array<() => void> = [];
  private streamComplete = false;
  private stopped = false;

  setSampleRate(sampleRate: number) {
    this.sampleRate = sampleRate > 0 ? sampleRate : 24000;
  }

  resume() {
    if (!this.stopped) {
      return;
    }
    this.stopped = false;
    this.streamComplete = false;
    this.pending = 0;
    this.leftover = new Uint8Array(0);
    if (this.context) {
      this.nextTime = this.context.currentTime;
    }
  }

  async push(bytes: Uint8Array) {
    if (bytes.byteLength === 0) {
      return;
    }

    if (this.stopped) {
      this.resume();
    }

    const merged = new Uint8Array(this.leftover.byteLength + bytes.byteLength);
    merged.set(this.leftover, 0);
    merged.set(bytes, this.leftover.byteLength);
    const evenLength = merged.byteLength - (merged.byteLength % 2);
    this.leftover = evenLength === merged.byteLength ? new Uint8Array(0) : merged.subarray(evenLength);
    if (evenLength < 2) {
      return;
    }

    const pcm = int16ToFloat32(merged.subarray(0, evenLength));
    const context = this.ensureContext();
    await context.resume().catch(() => undefined);
    const samples = resampleLinear(pcm, this.sampleRate, context.sampleRate);
    if (samples.length === 0) {
      return;
    }

    const buffer = context.createBuffer(1, samples.length, context.sampleRate);
    buffer.getChannelData(0).set(samples);
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.gain!);
    const startAt = Math.max(context.currentTime, this.nextTime);
    this.nextTime = startAt + buffer.duration;
    this.pending += 1;
    source.onended = () => {
      this.pending = Math.max(0, this.pending - 1);
      this.tryResolveEnded();
    };
    source.start(startAt);
  }

  markStreamComplete() {
    this.streamComplete = true;
    this.tryResolveEnded();
  }

  waitUntilEnded(): Promise<void> {
    if (this.stopped || (this.streamComplete && this.pending <= 0)) {
      return Promise.resolve();
    }

    return new Promise(resolve => {
      this.endedResolvers.push(resolve);
    });
  }

  stop() {
    this.stopped = true;
    this.pending = 0;
    this.nextTime = 0;
    this.leftover = new Uint8Array(0);
    this.streamComplete = true;
    if (this.gain && this.context) {
      try {
        this.gain.gain.setValueAtTime(0, this.context.currentTime);
        this.gain.disconnect();
      } catch {
        // The graph may already be torn down.
      }
      this.gain = this.context.createGain();
      this.gain.connect(this.context.destination);
    }
    this.flushEnded();
  }

  async close() {
    this.stop();
    const context = this.context;
    this.context = null;
    this.gain = null;
    if (context && context.state !== 'closed') {
      await context.close().catch(() => undefined);
    }
  }

  private ensureContext(): AudioContext {
    if (this.context && this.gain) {
      return this.context;
    }

    const AudioContextCtor = window.AudioContext
      || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) {
      throw new Error('Web Audio is not supported in this browser.');
    }

    this.context = new AudioContextCtor();
    this.gain = this.context.createGain();
    this.gain.connect(this.context.destination);
    this.nextTime = this.context.currentTime;
    this.stopped = false;
    this.streamComplete = false;
    return this.context;
  }

  private tryResolveEnded() {
    if (!this.streamComplete || this.pending > 0) {
      return;
    }
    this.flushEnded();
  }

  private flushEnded() {
    const resolvers = this.endedResolvers;
    this.endedResolvers = [];
    resolvers.forEach(resolve => resolve());
  }
}

export const int16PcmToFloat32ForTests = int16ToFloat32;
export const resampleLinearForTests = resampleLinear;
export { resampleLinear };
